import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import type { TypedInternalResponse, NitroFetchRequest } from 'nitropack';

import { tryUseNuxtApp, useRequestFetch, useRuntimeConfig } from '#app';

import { getRequestContextHeaders } from './RequestContext/RequestContext';

// this function is a wrapper for Orval and uses the $fetch hook. Nuxt context only.

// We can pass the same options as the $fetch hook
// https://nuxt.com/docs/api/utils/dollarfetch (UseFetchOptions)

export const apiFetch = async <T>(
  request: {
    url: string;
    method: NonNullable<Parameters<typeof $fetch<T>>[1]>['method'];
    params?: unknown;
    headers?: unknown;
    data?: unknown;
  } | string,
  opts?: Parameters<typeof $fetch<T>>[1],
) => {
  const url = typeof request === 'string' ? request : request.url;
  const method = typeof request === 'string' ? opts?.method || 'get' : request.method;
  const body = typeof request === 'string' ? opts?.body : request.data;

  const nuxtApp = tryUseNuxtApp();
  const fetcher: typeof $fetch = nuxtApp ? (useRequestFetch() as unknown as typeof $fetch) : $fetch;
  const debugRequests = import.meta.dev && Boolean(nuxtApp && useRuntimeConfig().public.DEBUG_REQUESTS);

  const data = await fetcher<T>(url, {
    ...(body ? { body: snakecaseKeys(body as Record<string, unknown>, { deep: true }) } : {}),
    ...((typeof request !== 'string' &&
      request?.params && {
        params: snakecaseKeys(request?.params as Record<string, unknown>, { exclude: ['includes[]'] }),
      }) as Record<string, unknown>),
    method,
    onRequest({ request }) {
      if (debugRequests) {
        console.log(`🔵 REQUEST apiFetch() → ${(method as string)?.toUpperCase?.() || 'GET'} ${request.toString()}`);
      }
    },
    onResponse({ response }) {
      if (debugRequests) {
        console.log(`🟢 RESPONSE apiFetch() ← ${response.status}`);
      }
    },
    ...opts,
    headers: {
      ...getRequestContextHeaders(),
      // Note: keep this in order as the last item, so it can be used as override!
      ...opts?.headers,
    },
  });

  return camelcaseKeys(data as Record<string, T>, { deep: true }) as TypedInternalResponse<NitroFetchRequest, T, 'get'>;
};
